import ajax from '@/lib/ajax';
import store from '@/store';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import { AppModule } from '@/store/Auth/AppModule';
import { AuthModule } from '@/store/Auth/AuthModule';
import DepositAccountSummary from '@/models/Accounts/DepositAccounts/DepositAccountSummary';
import Wallet from "@/models/Accounts/DepositAccounts/Wallets/Wallet";
import InstitutionAccount from '@/models/Accounts/InstitutionAccounts/InstitutionAccount';
import ThirdPartyAccount from '@/models/Accounts/ThirdPartyAccounts/ThirdPartyAccount';
import GetSingleAccountTransactionsRequest from '@/models/Reporting/Reports/GetSingleAccountTransactionsRequest';
import PermissionUtils from '@/lib/permissionUtils';

export interface IReportsState {
    loadingReport: boolean;
    loadingReportFailed: boolean;
    loadingAccountsForReport: boolean;
    depositAccountsForReport: DepositAccountSummary[];
    depositAccountWalletsForReport: Wallet[];
    institutionAccountsForReport: InstitutionAccount[]
    thirdPartyAccountsForReport: ThirdPartyAccount[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'ReportsModule', namespaced: true })
class ReportsStore extends VuexModule implements IReportsState {

    public loadingReport: boolean = true;
    public loadingReportFailed: boolean = false;
    public loadingAccountsForReport: boolean = false;
    public depositAccountsForReport: DepositAccountSummary[] = [];
    public depositAccountWalletsForReport: Wallet[] = [];
    public institutionAccountsForReport: InstitutionAccount[] = [];
    public thirdPartyAccountsForReport: ThirdPartyAccount[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getEndOfDayBalancesProjection(data: { date: string, accountType: string }) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        await ajax.get(`/portal-backend/fi-api/projections/eod-balance?date=${data.date}&accountType=${data.accountType}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `all-${data.accountType.toLowerCase()}-accounts-end-of-day-balances-report-${data.date}.csv`);
            fileLink.setAttribute('id', `all-${data.accountType.toLowerCase()}-accounts-end-of-day-balances-report-${data.date}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getEndOfDayAccountStateProjection(data: { date: string }) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        await ajax.get(`/portal-backend/fi-api/projections/eod-account-state-deposit-fixed-term?date=${data.date}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `all-end-of-day-account-state-report-${data.date}.csv`);
            fileLink.setAttribute('id', `all-end-of-day-account-state-report-${data.date}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getAllAccountTransactionsProjection(data: { date: string, accountType: string }) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        const cardIntegrationParameter = PermissionUtils.hasGpsCardIntegration() ? `&cardIntegration=GPS` : "";

        await ajax.get(`/portal-backend/fi-api/projections/transactions?date=${data.date}&accountType=${data.accountType}${cardIntegrationParameter}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `all-${data.accountType.toLowerCase()}-accounts-transactions-report-${data.date}.csv`);
            fileLink.setAttribute('id', `all-${data.accountType.toLowerCase()}-accounts-transactions-report-${data.date}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getCustomerAccountTransactionsProjection(request: GetSingleAccountTransactionsRequest) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        await ajax.get(`/portal-backend/fi-api/projections/customer-account-transactions?fromDate=${request.fromDate}&toDate=${request.toDate}&accountId=${request.accountId}&accountName=${request.accountName}&walletId=${request.walletId}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            // Remove whitespace from name for safety as it will be used in the filename
            const accountNameNoSpaces = request.accountName.replace(/\s+/g, '');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            fileLink.setAttribute('id', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getInstitutionAccountTransactionsProjection(request: GetSingleAccountTransactionsRequest) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        await ajax.get(`/portal-backend/fi-api/projections/institution-account-transactions?fromDate=${request.fromDate}&toDate=${request.toDate}&accountId=${request.accountId}&accountName=${request.accountName}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            // Remove whitespace from name for safety as it will be used in the filename
            const accountNameNoSpaces = request.accountName.replace(/\s+/g, '');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            fileLink.setAttribute('id', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getThirdPartyAccountTransactionsProjection(request: GetSingleAccountTransactionsRequest) {

        this.setLoadingReportFailed(false);
        this.setLoadingReport(true);

        await ajax.get(`/portal-backend/fi-api/projections/third-party-account-transactions?fromDate=${request.fromDate}&toDate=${request.toDate}&accountId=${request.accountId}&accountName=${request.accountName}`, { responseType : 'blob', timeout: 30000 })
        .then(response => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            // Remove whitespace from name for safety as it will be used in the filename
            const accountNameNoSpaces = request.accountName.replace(/\s+/g, '');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            fileLink.setAttribute('id', `${accountNameNoSpaces}-transactions-report-from-${request.fromDate}-to-${request.toDate}.csv`);
            document.body.appendChild(fileLink);
            this.setLoadingReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingReportFailed(true);
            this.setLoadingReport(false);
        });
    }

    @Action
    async getDepositAccountsForReport() {

        this.setLoadingAccountsForReport(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {

            // Note: Here we use the standard GET /accounts endpoint instead of the POST /ux/customer-accounts that
            // is elsewhere because we don't need the account number in this case
            await ajax.get(`/portal-backend/ux-api/accounts`)
            .then(response => {
                this.context.commit("setCustomerAccountsForReport", response.data.accounts);
                this.setLoadingAccountsForReport(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingAccountsForReport(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts`)
            .then(response => {
                this.context.commit("setCustomerAccountsForReport", response.data.results);
                this.setLoadingAccountsForReport(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingAccountsForReport(false);
            });
        }
    }

    @Action
    async getDepositAccountWalletsForReport(accountId: string) {

        this.setLoadingAccountsForReport(true);

        // Always get wallets from FI because it contains both wallets created in FI and UX
        await ajax.get(`/portal-backend/fi-api/accounts/${accountId}/wallets`)
        .then(response => {
            this.context.commit("setDepositAccountWalletsForReport", response.data.results);
            this.setLoadingAccountsForReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAccountsForReport(false);
        });
    }

    @Action
    async getInstitutionAccountsForReport() {

        this.setLoadingAccountsForReport(true);

        await ajax.get(`/portal-backend/fi-api/internal/institutional-accounts`)
        .then(response => {
            this.context.commit("setInstitutionAccountsForReport", response.data.results);
            this.setLoadingAccountsForReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAccountsForReport(false);
        });
    }

    @Action
    async getThirdPartyAccountsForReport() {

        this.setLoadingAccountsForReport(true);

        await ajax.get(`/portal-backend/fi-api/internal/third-party-accounts`)
        .then(response => {
            this.context.commit("setThirdPartyAccountsForReport", response.data.accounts);
            this.setLoadingAccountsForReport(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingAccountsForReport(false);
        });
    }

    @Action
    cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loadingReport = true;
        this.loadingReportFailed = false;
        this.loadingAccountsForReport = true;
        this.depositAccountsForReport = [];
        this.depositAccountWalletsForReport = [];
        this.institutionAccountsForReport = [];
        this.thirdPartyAccountsForReport = [];
    }

    @Mutation
    private async setLoadingReport(loading: boolean) {
        this.loadingReport = loading;
    }

    @Mutation
    private async setLoadingReportFailed(loading: boolean) {
        this.loadingReportFailed = loading;
    }

    @Mutation
    private async setLoadingAccountsForReport(loading: boolean) {
        this.loadingAccountsForReport = loading;
    }

    @Mutation
    private async setCustomerAccountsForReport(response: DepositAccountSummary[]) {
        if (response) {
            this.depositAccountsForReport = response;
        }
    }

    @Mutation
    private async setDepositAccountWalletsForReport(response: []) {
        if (response) {
            this.depositAccountWalletsForReport = response;
        }
    }

    @Mutation
    private async setInstitutionAccountsForReport(response: InstitutionAccount[]) {
        if (response) {
            this.institutionAccountsForReport = response;
        }
    }

    @Mutation
    private async setThirdPartyAccountsForReport(response: ThirdPartyAccount[]) {
        if (response) {
            this.thirdPartyAccountsForReport = response;
        }
    }

}

export const ReportsModule = getModule(ReportsStore);